import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker (same version as react-pdf uses)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Extract text content from a PDF file in the browser.
 * Uses pdfjs-dist to parse the PDF and extract text from each page.
 *
 * @param {File} file - PDF file to extract text from
 * @param {Object} options - Extraction options
 * @param {number} options.maxPages - Maximum pages to extract (default: 50)
 * @param {function} options.onProgress - Progress callback (pageNumber, totalPages)
 * @returns {Promise<{text: string, pageCount: number}>}
 */
export async function extractTextFromPdf(file, options = {}) {
  const { maxPages = 50, onProgress } = options;

  // Convert File to ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();

  // Load the PDF document
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pageCount = pdf.numPages;
  const pagesToExtract = Math.min(pageCount, maxPages);

  const textParts = [];

  for (let i = 1; i <= pagesToExtract; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map(item => item.str)
      .join(' ');
    textParts.push(pageText);

    if (onProgress) {
      onProgress(i, pagesToExtract);
    }
  }

  return {
    text: textParts.join('\n\n'),
    pageCount,
  };
}

/**
 * Check if a file is a valid PDF by reading its header.
 * @param {File} file - File to check
 * @returns {Promise<boolean>}
 */
export async function isPdfFile(file) {
  if (file.type === 'application/pdf') {
    return true;
  }

  // Check magic bytes if MIME type is missing
  const header = await file.slice(0, 5).text();
  return header === '%PDF-';
}
